import { ElementRef } from '@angular/core';
import flatpickr from 'flatpickr';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { startOfDay, startOfMonth, subMonths } from 'date-fns';

type MaybeRef = ElementRef<HTMLInputElement> | null | undefined;

export type InitDatePickerOptions = {
  dateInput?: MaybeRef;
  secondaryDateInput?: MaybeRef;

  // Primary datepicker
  getDate?: () => string | undefined;
  setDate?: (v: string) => void;

  // Secondary datepicker
  getSecondaryDate?: () => string | undefined;
  setSecondaryDate?: (v: string) => void;

  // Optional overrides
  minDate?: 'today' | string | Date;
  maxDate?: 'today' | string | Date;
  dateFormat?: string;
  altFormat?: string;
  altInput?: boolean;
  yearsAhead?: number;
  yearsBack?: number;
  startYear?: number;
  endYear?: number;
  disabled?: boolean;
  enableTime?: boolean;
  noCalendar?: boolean;
  time24hr?: boolean;
  showMonths?: number;
  mode?: 'single' | 'range';
  onClose?: (selectedDates: Date[], dateStr: string) => void;

  // Inline mode options
  inline?: boolean;
  inlineContainer?: HTMLElement;
  onSave?: (selectedDates: Date[]) => void;
  onClear?: () => void;
};

export type InitDatePickerResult = {
  dateInstance?: FlatpickrInstance;
  secondaryDateInstance?: FlatpickrInstance;
  destroy: () => void;
};

export function isDateBeforeMinDate(value: string | Date, minDate: 'today' | string | Date): boolean {
  const valueDate = startOfDay(value instanceof Date ? value : new Date(value));
  const boundaryDate = startOfDay(minDate === 'today' ? new Date() : new Date(minDate));

  return valueDate < boundaryDate;
}

export function preserveLoadedDateTimeWithMinDate(
  instance: FlatpickrInstance,
  value: string | Date,
  minDate: 'today' | string | Date
): void {
  const fp = instance as any;
  const resolvedMinDate = minDate === 'today' ? startOfDay(new Date()) : new Date(minDate);
  const selectedDate = value instanceof Date ? value : instance.parseDate(value, fp.config.dateFormat);

  if (!selectedDate) {
    instance.set('minDate', resolvedMinDate);
    return;
  }

  instance.set('minDate', undefined);
  instance.setDate(selectedDate, false);
  fp.config.minDate = resolvedMinDate;

  fp.selectedDates = [selectedDate];
  fp.latestSelectedDateObj = selectedDate;
  fp._input.value = instance.formatDate(selectedDate, fp.config.dateFormat);

  if (fp.altInput) {
    fp.altInput.value = instance.formatDate(selectedDate, fp.config.altFormat || fp.config.dateFormat);
  }

  if (fp.hourElement) {
    fp.hourElement.value = String(selectedDate.getHours()).padStart(2, '0');
  }

  if (fp.minuteElement) {
    fp.minuteElement.value = String(selectedDate.getMinutes()).padStart(2, '0');
  }

  if (fp.secondElement) {
    fp.secondElement.value = String(selectedDate.getSeconds()).padStart(2, '0');
  }

  instance.jumpToDate(selectedDate, false);
  instance.redraw();
}

export function initDatePicker(opts: InitDatePickerOptions): InitDatePickerResult {
  const {
    dateInput,
    secondaryDateInput,
    getDate: getDate,
    setDate: setDate,
    getSecondaryDate: getSecondaryDate,
    setSecondaryDate: setSecondaryDate,
    minDate = 'today',
    maxDate,
    dateFormat = 'Y/m/d',
    altFormat = 'Y/m/d',
    altInput = true,
    yearsAhead = 20,
    yearsBack = 0,
    startYear,
    endYear,
    disabled = false,
    enableTime = false,
    noCalendar = false,
    time24hr = true,
    showMonths = 1,
    mode = 'single',
    onClose,
    inline = false,
    inlineContainer,
    onSave,
    onClear
  } = opts;

  const nowYear = new Date().getFullYear();
  const panelStartYear = typeof startYear === 'number' ? startYear : nowYear - yearsBack;
  const panelEndYear = typeof endYear === 'number' ? endYear : nowYear + yearsAhead;
  const instances: { dateInstance?: FlatpickrInstance; secondaryDateInstance?: FlatpickrInstance } = {};

  // Inline range mode: render calendar inline without an input
  if (inline && inlineContainer) {
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'text';
    hiddenInput.style.display = 'none';
    inlineContainer.appendChild(hiddenInput);

    instances.dateInstance = flatpickr(hiddenInput, {
      dateFormat,
      mode: mode as 'single' | 'range',
      showMonths,
      inline: true,
      minDate,
      maxDate,
      monthSelectorType: 'static',
      disableMobile: true,
      locale: { firstDayOfWeek: 1 },
      appendTo: inlineContainer,
      defaultDate: undefined,
      onReady: (_, __, fp) => {
        const previousAndCurrentMonth = (fp: FlatpickrInstance, showMonths: number) => {
          const currentMonth = startOfMonth(new Date());
          const firstVisibleMonth = showMonths >= 2 ? startOfMonth(subMonths(currentMonth, 1)) : currentMonth;

          fp.jumpToDate(firstVisibleMonth, false);
          fp.redraw();
        };
        previousAndCurrentMonth(fp, showMonths);

        // Add year arrow and disable year input for each month section
        const yearWrappers = fp.calendarContainer?.querySelectorAll('.numInputWrapper');
        yearWrappers?.forEach(yearWrapper => {
          const yearInput = yearWrapper.querySelector('.cur-year') as HTMLInputElement | null;
          if (yearInput) {
            yearInput.setAttribute('disabled', 'true');
            yearInput.style.pointerEvents = 'none';
            yearInput.style.background = 'transparent';
            yearInput.style.color = '#4D4749';
            yearInput.style.border = 'none';
          }
          const arrow = document.createElement('span');
          arrow.classList.add('custom-year-arrow');
          yearWrapper.insertAdjacentElement('afterend', arrow);
        });

        // Add Clear + Save footer
        const footerHtml = `
          <div class="flatpickr-footer">
            <button class="clear-btn" type="button">Clear</button>
            <button class="save-btn" type="button">Save</button>
          </div>
        `;
        fp.calendarContainer?.insertAdjacentHTML('beforeend', footerHtml);

        const clearBtn = fp.calendarContainer?.querySelector('.clear-btn') as HTMLButtonElement | null;
        clearBtn?.addEventListener('click', () => {
          fp.clear();
          previousAndCurrentMonth(fp, showMonths);
          onClear?.();
        });

        const saveBtn = fp.calendarContainer?.querySelector('.save-btn') as HTMLButtonElement | null;
        saveBtn?.addEventListener('click', () => {
          if (fp.selectedDates.length === 2) {
            onSave?.(fp.selectedDates);
          }
        });
      }
    }) as unknown as FlatpickrInstance;

    return {
      dateInstance: instances.dateInstance,
      destroy: () => {
        instances.dateInstance?.destroy();
        hiddenInput.remove();
      }
    };
  }

  if (dateInput?.nativeElement) {
    const initialDate = getDate?.() || undefined;
    // Keep the loaded past datetime visible and selected while still blocking new
    // selections before minDate.
    let effectiveMinDate: 'today' | string | Date | undefined = minDate;
    let shouldApplyMinDateAfterInit = false;

    if (initialDate && minDate) {
      if (isDateBeforeMinDate(initialDate, minDate)) {
        effectiveMinDate = undefined;
        shouldApplyMinDateAfterInit = true;
      }
    }

    instances.dateInstance = flatpickr(dateInput.nativeElement, {
      dateFormat,
      altFormat,
      altInput,
      defaultDate: initialDate,
      minDate: effectiveMinDate,
      maxDate,
      monthSelectorType: 'static',
      clickOpens: !disabled,
      allowInput: !disabled,
      disableMobile: true,
      enableTime,
      noCalendar,
      time_24hr: time24hr,
      showMonths,
      mode,
      onChange: (selectedDates, dateStr) => setDate?.(dateStr),
      onClose: onClose ? (selectedDates, dateStr) => onClose(selectedDates, dateStr) : undefined,
      onReady: (_, __, instance) => {
        if (shouldApplyMinDateAfterInit && minDate) {
          preserveLoadedDateTimeWithMinDate(instance, instance.selectedDates[0] ?? initialDate!, minDate);
        }
        // Apply disabled styling to the altInput if disabled
        if (disabled && instance.altInput) {
          instance.altInput.disabled = true;
          instance.altInput.style.opacity = '0.6';
          instance.altInput.style.color = '#6B6D71';
          instance.altInput.style.backgroundColor = '#ECEEF0';
          instance.altInput.style.cursor = 'not-allowed';
        }
        const arrow = initYearArrow(instance);
        const panel = initYearPanel(instance, panelStartYear, panelEndYear);
        const currentMonth = instance.calendarContainer?.querySelector('.flatpickr-current-month') as HTMLElement | null;
        arrow.addEventListener('click', () => {
          const isOpen = panel.style.display === 'none';
          panel.style.display = isOpen ? 'grid' : 'none';
          if (currentMonth) {
            currentMonth.classList.toggle('year-panel-open', isOpen);
          }
          highlightActiveYear(panel, instance);
        });
        if (enableTime) {
          initTimeHeader(instance);
        }
        initClearBtn(instance, dateInput, enableTime, () => setDate?.(''));
      },
      onOpen: (_, __, instance) => {
        const panel = instance.calendarContainer?.querySelector('.custom-year-panel') as HTMLDivElement | null;
        if (panel) highlightActiveYear(panel, instance);
      }
    });
  }

  if (secondaryDateInput?.nativeElement) {
    instances.secondaryDateInstance = flatpickr(secondaryDateInput.nativeElement, {
      dateFormat,
      altFormat,
      altInput,
      defaultDate: getSecondaryDate?.() || undefined,
      minDate,
      maxDate,
      monthSelectorType: 'static',
      disableMobile: true,
      enableTime,
      noCalendar,
      time_24hr: time24hr,
      onChange: (_, dateStr) => setSecondaryDate?.(dateStr),
      onReady: (_, __, instance) => {
        const arrow = initYearArrow(instance);
        const panel = initYearPanel(instance, panelStartYear, panelEndYear);
        const currentMonth = instance.calendarContainer?.querySelector('.flatpickr-current-month') as HTMLElement | null;
        arrow.addEventListener('click', () => {
          const isOpen = panel.style.display === 'none';
          panel.style.display = isOpen ? 'grid' : 'none';
          if (currentMonth) {
            currentMonth.classList.toggle('year-panel-open', isOpen);
          }
          highlightActiveYear(panel, instance);
        });
        if (enableTime) {
          initTimeHeader(instance);
        }
        initClearBtn(instance, secondaryDateInput, enableTime, () => setSecondaryDate?.(''));
      },
      onOpen: (_, __, instance) => {
        const panel = instance.calendarContainer?.querySelector('.custom-year-panel') as HTMLDivElement | null;
        if (panel) highlightActiveYear(panel, instance);
      }
    });
  }

  return {
    ...instances,
    destroy: () => {
      instances.dateInstance?.destroy();
      instances.secondaryDateInstance?.destroy();
    }
  };
}

function initYearArrow(instance: any) {
  const arrow = document.createElement('span');
  arrow.classList.add('custom-year-arrow');

  const yearWrapper = instance.calendarContainer?.querySelector('.numInputWrapper');
  if (yearWrapper) {
    const yearInput = yearWrapper.querySelector('.cur-year') as HTMLInputElement | null;
    if (yearInput) {
      yearInput.setAttribute('disabled', 'true');
      yearInput.style.pointerEvents = 'none';
      yearInput.style.background = 'transparent';
      yearInput.style.color = '#4D4749';
      yearInput.style.border = 'none';
    }
    yearWrapper.insertAdjacentElement('afterend', arrow);
  }

  return arrow;
}

function initYearPanel(instance: any, startYear: number, endYear: number) {
  const calendar = instance.calendarContainer as HTMLElement | null;
  const yearPanel = document.createElement('div');
  yearPanel.classList.add('custom-year-panel');
  yearPanel.style.display = 'none';

  for (let y = startYear; y <= endYear; y++) {
    const yearBtn = document.createElement('button');
    yearBtn.textContent = y.toString();
    yearBtn.classList.add('year-option');
    if (y === startYear) yearBtn.classList.add('active');

    yearBtn.addEventListener('click', () => {
      const newDate = new Date();
      newDate.setFullYear(y);
      newDate.setMonth(instance.currentMonth);
      newDate.setDate(1);

      instance.clear();
      instance.setDate(newDate, false);
      yearPanel.style.display = 'none';
      highlightActiveYear(yearPanel, instance);
    });

    yearPanel.appendChild(yearBtn);
  }

  calendar?.appendChild(yearPanel);
  highlightActiveYear(yearPanel, instance);
  return yearPanel;
}

function initClearBtn(instance: any, dateInput: ElementRef | undefined | null, enableTime: boolean, onClearModel: () => void) {
  const footerHtml = enableTime
    ? `
    <div class="flatpickr-footer">
      <button class="clear-btn" type="button">Clear</button>
      <button class="save-btn" type="button">Save</button>
    </div>
    `
    : `
    <div class="flatpickr-footer">
      <button class="clear-btn" type="button">Clear</button>
    </div>
    `;

  instance.calendarContainer?.insertAdjacentHTML('beforeend', footerHtml);

  const clearBtn = instance.calendarContainer?.querySelector('.clear-btn') as HTMLButtonElement | null;
  clearBtn?.addEventListener('click', () => {
    instance.clear();
    onClearModel();
    if (dateInput?.nativeElement) {
      (dateInput.nativeElement as HTMLInputElement).value = '';
    }
    instance.close();
  });

  if (enableTime) {
    const saveBtn = instance.calendarContainer?.querySelector('.save-btn') as HTMLButtonElement | null;
    saveBtn?.addEventListener('click', () => {
      instance.close();
    });
  }
}

function highlightActiveYear(panel: HTMLElement, instance: any) {
  const currentYear = instance?.currentYear ?? new Date().getFullYear();
  panel.querySelectorAll('.year-option').forEach(btn => btn.classList.remove('active'));
  const active = Array.from(panel.querySelectorAll<HTMLButtonElement>('.year-option'))
    .find(b => Number(b.textContent) === currentYear);
  active?.classList.add('active');
}

export type InitInlineRangePickerOptions = {
  container: HTMLElement;
  minDate?: string | Date;
  maxDate?: string | Date;
  dateFormat?: string;
  onSave?: (selectedDates: Date[]) => void;
  onClear?: () => void;
};

export type InitInlineRangePickerResult = {
  instance: FlatpickrInstance;
  destroy: () => void;
};

export function initInlineRangePicker(opts: InitInlineRangePickerOptions): InitInlineRangePickerResult {
  const {
    container,
    minDate,
    maxDate,
    dateFormat = 'Y/m/d',
    onSave,
    onClear
  } = opts;

  // Create a hidden input for flatpickr to bind to
  const hiddenInput = document.createElement('input');
  hiddenInput.type = 'text';
  hiddenInput.style.display = 'none';
  container.appendChild(hiddenInput);

  const instance = flatpickr(hiddenInput, {
    dateFormat,
    mode: 'range',
    showMonths: 2,
    inline: true,
    minDate,
    maxDate,
    monthSelectorType: 'static',
    disableMobile: true,
    locale: { firstDayOfWeek: 1 },
    appendTo: container,
    onReady: (_, __, fp) => {
      // Add Clear + Save footer
      const footerHtml = `
        <div class="flatpickr-footer">
          <button class="clear-btn" type="button">Clear</button>
          <button class="save-btn" type="button">Save</button>
        </div>
      `;
      fp.calendarContainer?.insertAdjacentHTML('beforeend', footerHtml);

      const clearBtn = fp.calendarContainer?.querySelector('.clear-btn') as HTMLButtonElement | null;
      clearBtn?.addEventListener('click', () => {
        fp.clear();
        onClear?.();
      });

      const saveBtn = fp.calendarContainer?.querySelector('.save-btn') as HTMLButtonElement | null;
      saveBtn?.addEventListener('click', () => {
        if (fp.selectedDates.length === 2) {
          onSave?.(fp.selectedDates);
        }
      });
    }
  }) as unknown as FlatpickrInstance;

  return {
    instance,
    destroy: () => {
      instance.destroy();
      hiddenInput.remove();
    }
  };
}

function initTimeHeader(instance: any) {
  const timeContainer = instance.calendarContainer?.querySelector('.flatpickr-time') as HTMLElement | null;
  if (timeContainer) {
    const timeHeader = document.createElement('div');
    timeHeader.classList.add('flatpickr-time-header');
    timeHeader.innerHTML = `
      <svg class="time-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.9987 1.3335C4.3227 1.3335 1.33203 4.32416 1.33203 8.00016C1.33203 11.6762 4.3227 14.6668 7.9987 14.6668C11.6747 14.6668 14.6654 11.6762 14.6654 8.00016C14.6654 4.32416 11.6747 1.3335 7.9987 1.3335ZM7.9987 13.3335C5.05803 13.3335 2.66536 10.9408 2.66536 8.00016C2.66536 5.0595 5.05803 2.66683 7.9987 2.66683C10.9394 2.66683 13.332 5.0595 13.332 8.00016C13.332 10.9408 10.9394 13.3335 7.9987 13.3335Z" fill="#9FA1A4"/>
        <path d="M8.66536 4.6665H7.33203V8.27584L9.52736 10.4712L10.47 9.5285L8.66536 7.72384V4.6665Z" fill="#9FA1A4"/>
      </svg>
      <span class="time-label">Time</span>
    `;
    timeContainer.parentElement?.insertBefore(timeHeader, timeContainer);
  }
}